class ComponentAnnouncement extends Theme.Component {
  #interval = null;
  #index = 0;
  #stopped = false;
  #touchStartX = null;
  #refreshFreeShipping = () => this.refreshFreeShipping();
  #pause = () => this.stopRotation();
  #resume = () => this.resumeRotation();
  #touchStart = (event) => (this.#touchStartX = event.touches[0].clientX);
  #touchEnd = (event) => this.handleSwipe(event.changedTouches[0].clientX);
  #trackWidth = null;
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (entry.contentRect.width === this.#trackWidth) return;
    this.#trackWidth = entry.contentRect.width;
    this.updateCrowding();
  });

  connectedCallback() {
    super.connectedCallback();
    this.messages = this.querySelectorAll('.announcement-message');
    this.track = this.querySelector('.announcement-track');
    if (this.messages.length) this.show(0);
    requestAnimationFrame(() => this.classList.add('is-loaded'));

    if (this.messages.length > 1) {
      this.addEventListener('mouseenter', this.#pause);
      this.addEventListener('mouseleave', this.#resume);
      this.addEventListener('focusin', this.#pause);
      this.addEventListener('focusout', this.#resume);
      this.track.addEventListener('touchstart', this.#touchStart, { passive: true });
      this.track.addEventListener('touchend', this.#touchEnd);
      this.#resizeObserver.observe(this.track);
      document.fonts.ready.then(() => this.updateCrowding());
      this.resumeRotation();
    }

    if (this.querySelector('[data-free-shipping]')) {
      document.addEventListener('addtocart', this.#refreshFreeShipping);
      document.addEventListener('cart:change', this.#refreshFreeShipping);
    }
  }

  disconnectedCallback() {

    super.disconnectedCallback();
    this.stopRotation();
    this.#resizeObserver.disconnect();
    document.removeEventListener('addtocart', this.#refreshFreeShipping);
    document.removeEventListener('cart:change', this.#refreshFreeShipping);
  }

  get prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  resumeRotation() {
    if (this.#stopped || this.#interval || this.prefersReducedMotion) return;
    const speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
    this.#interval = setInterval(() => this.show(this.#index + 1), speed);
  }

  stopRotation() {
    clearInterval(this.#interval);
    this.#interval = null;
  }

  takeControl() {
    this.#stopped = true;
    this.stopRotation();
    this.track.setAttribute('aria-live', 'polite');
  }

  next() {
    this.takeControl();
    this.show(this.#index + 1);
  }

  previous() {
    this.takeControl();
    this.show(this.#index - 1);
  }

  handleSwipe(endX) {
    if (this.#touchStartX === null) return;
    const distance = endX - this.#touchStartX;
    this.#touchStartX = null;
    if (Math.abs(distance) < 40) return;
    distance < 0 ? this.next() : this.previous();
  }

  show(index) {
    const count = this.messages.length;
    this.#index = (index + count) % count;

    this.messages.forEach((message, i) => {
      const active = i === this.#index;
      message.classList.toggle('is-active', active);
      message.toggleAttribute('inert', !active);
      message.setAttribute('aria-hidden', String(!active));
    });

    this.syncCrowdedState();
  }

  updateCrowding() {
    this.messages.forEach((message) => message.classList.remove('state--crowded'));

    const inset = parseFloat(getComputedStyle(this.messages[0]).paddingLeft) || 0;
    const roomBesideArrows = this.track.clientWidth - inset * 2;

    this.messages.forEach((message) => {
      message.classList.toggle('state--crowded', this.naturalWidth(message) > roomBesideArrows + 1);
    });

    this.syncCrowdedState();
  }

  naturalWidth(message) {
    message.style.whiteSpace = 'nowrap';
    const range = document.createRange();
    range.selectNodeContents(message);
    const width = range.getBoundingClientRect().width;
    message.style.whiteSpace = '';
    return width;
  }

  syncCrowdedState() {
    const active = this.messages[this.#index];
    this.classList.toggle('state--crowded', Boolean(active?.classList.contains('state--crowded')));
  }

  refreshFreeShipping() {
    const sectionId = this.dataset.sectionId;

    return fetch(`${window.location.pathname}?sections=${sectionId}`)
      .then((response) => response.json())
      .then((sections) => {
        const doc = new DOMParser().parseFromString(sections[sectionId], 'text/html');

        this.querySelectorAll('[data-free-shipping]').forEach((element) => {
          const fresh = doc.querySelector(`[data-free-shipping="${element.dataset.freeShipping}"]`);
          if (fresh) element.innerHTML = fresh.innerHTML;
        });

        if (this.messages.length > 1) this.updateCrowding();
      })
      .catch((error) => console.error('Announcement: free shipping refresh failed', error));
  }
}

customElements.define('component-announcement', ComponentAnnouncement);
